<template>
    <grid ref="grid" :column-defs="documentColumnDefs" :fetch-callback="fetchCallback">
        <div class="d-flex flex-column">
            <b-alert show variant="info" v-if="isTestEditorUser"
                >Check out our updated editor! Migrate your existing templates today.
                <a href="https://support.lodgix.com/en/articles/10508558-migrating-document-templates">Learn more</a>
            </b-alert>
            <div class="d-flex align-items-center">
                <responsive-button :icon="['fas', 'arrows-rotate']" @click="refresh" variant="primary">
                    Refresh
                </responsive-button>
                <responsive-button :icon="['fas', 'plus']" :href="templateAddLink" variant="primary"
                    >Add Template</responsive-button
                >
            </div>
        </div>
        <modal :id="restoreModalId" title="Restore document" @ok="restoreDocument">
            <p>
                Warning: You are about to restore this document to its original state. All customizations will be lost.
                This cannot be undone. Would you like to proceed?
            </p>
        </modal>
    </grid>
</template>

<script>
import API from '../../../api';
import {
    DOCUMENT_REPORT_TEMPLATE_CLONE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_DEFAULT_AGREEMENT_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_GRID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_HIDE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_UNHIDE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_RESTORE_ENDPOINT,
} from '../../../endpoints';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import AdditionalInfoCellRenderer from './additional-info-cell-renderer.vue';
import Modal from '../../generic/modal/modal.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'DocumentTemplates',
    components: {
        Grid,
        ResponsiveButton,
        Modal,
        actionsCellRenderer: ActionsCellRenderer,
        additionalInfoCellRenderer: AdditionalInfoCellRenderer,
    },
    data() {
        return {
            restoreModalId: null,
            templateAddLink: '/app/document/0/edit/',
            columnDefs: [
                {
                    field: 'report_name',
                    headerName: 'Name',
                    sort: 'asc',
                },
                {
                    field: 'report_type.name',
                    headerName: 'Type',
                },
                {
                    field: 'report_orientation.name',
                    headerName: 'Orientation',
                },
                {
                    field: 'is_migrated',
                    headerName: 'Migrated',
                    type: 'yesNoColumn',
                    maxWidth: 120,
                    minWidth: 120,
                },
                {
                    field: 'default_report_type_name',
                    headerName: 'Additional Info',
                    minWidth: 250,
                    cellRenderer: 'additionalInfoCellRenderer',
                    cellRendererParams: {
                        setDefaultAgreement: async (template) => {
                            try {
                                await API.post(DOCUMENT_REPORT_TEMPLATE_DEFAULT_AGREEMENT_ENDPOINT(template.id));
                                this.successToast({
                                    text: `${template.report_name} is now the default rental agreement template.`,
                                });
                            } catch (error) {
                                this.errorToast({ text: `Could not set default rental agreement template.` });
                            }
                            await this.refresh();
                        },
                    },
                },
                {
                    headerName: '',
                    maxWidth: 220,
                    minWidth: 220,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        clone: async (template) => {
                            try {
                                await API.post(DOCUMENT_REPORT_TEMPLATE_CLONE_ENDPOINT(template.id));
                                this.successToast({ text: 'Template successfully cloned.' });
                            } catch (error) {
                                this.errorToast({ text: `Could not clone template.` });
                            }
                            await this.refresh();
                        },
                        delete: async (template) => {
                            try {
                                await API.delete(DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(template.id));
                            } catch (error) {
                                this.errorToast({ text: `Could not delete template.` });
                            }
                            await this.refresh();
                        },
                        restore: async (template) => {
                            this.restoreModalId = `restore-todo-modal` + template.id;
                            this.$nextTick(() => {
                                this.$bvModal.show(this.restoreModalId);
                            });
                        },
                    },
                },
            ],
        };
    },
    computed: {
        ...mapGetters('header', ['isTestEditorUser']),
        documentColumnDefs() {
            return this.isTestEditorUser
                ? this.columnDefs
                : this.columnDefs.filter((column) => column.field !== 'is_migrated');
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(DOCUMENT_REPORT_TEMPLATE_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async restoreDocument() {
            const id = this.restoreModalId.split('restore-todo-modal')[1];
            try {
                await API.post(DOCUMENT_REPORT_TEMPLATE_RESTORE_ENDPOINT(id));
                this.successToast({ text: 'Template successfully restored.' });
            } catch (error) {
                this.errorToast({ text: `Could not restore template.` });
            }
            await this.refresh();
        },
    },
};
</script>
